/**
 * WAREHOUSE_TERMINAL — kho.
 *
 * Wired against `routes/inventory.ts`:
 *   Nhận hàng   POST /v1/inventory/receipts   (against a PO or free-form)
 *   Kiểm kê     POST /v1/inventory/counts + PUT items + POST submit
 *   Xuất NL     POST /v1/inventory/issues
 *   Hao hụt     POST /v1/inventory/wastages   (reason MANDATORY)
 *
 * QUANTITIES ARE MILLI-UNITS ON THE WIRE (integer; 1.5 kg = 1500). The forms
 * accept decimals and convert by STRING manipulation (`parseQtyToMilli`), never
 * by `parseFloat(x) * 1000` — 1.1 * 1000 is 1100.0000000000002 in IEEE-754 and
 * a warehouse ledger must not drift by float crumbs.
 *
 * All four postings are ONLINE-ONLY on purpose: each needs a server-checked
 * Idempotency-Key and moves stock value; queuing them offline could post a
 * receipt against a PO another clerk already received. The one offline-capable
 * action here remains "báo hết món" (PRODUCT_AVAILABILITY).
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  SearchField,
  Select,
  Tabs,
  Textarea,
  useToast,
} from '@sonauto/ui';
import { useApi, useAuth } from '../../auth/AuthProvider';
import { useAsync, errorMessage } from '../../lib/hooks';
import { useOutbox } from '../../offline/OutboxProvider';
import { Chrome } from '../../components/Chrome';
import { todayIso, vnd } from '../../lib/format';
import type {
  InvIngredient,
  InvPoItem,
  InvPurchaseOrder,
  ReceiptItemInput,
  StockCountItem,
  WastageType,
} from '../../lib/types';
import { WASTAGE_TYPES } from '../../lib/types';

type Tab = 'RECEIVE' | 'COUNT' | 'ISSUE' | 'WASTAGE' | 'AVAILABILITY';

// ---------------------------------------------------------------------------
// Milli-unit conversion — integer math only
// ---------------------------------------------------------------------------

/**
 * '1.5' -> 1500, '0.125' -> 125, '2' -> 2000. STRING manipulation, not float
 * math: the integer part and up to three fraction digits are parsed separately
 * and recombined as integers, so no IEEE-754 rounding can leak into the ledger.
 * Accepts ',' as decimal separator (vi-VN keyboards). Returns null on garbage.
 */
export function parseQtyToMilli(input: string): number | null {
  const match = /^(\d+)(?:[.,](\d{1,3}))?$/.exec(input.trim());
  if (match === null) return null;
  const whole = Number.parseInt(match[1] ?? '0', 10);
  // Right-pad the fraction to exactly 3 digits: '5' -> '500', '25' -> '250'.
  const fraction = Number.parseInt(((match[2] ?? '') + '000').slice(0, 3), 10);
  if (!Number.isSafeInteger(whole) || whole > 1_000_000_000) return null;
  return whole * 1000 + fraction;
}

/** 1500 -> '1.5'; 2000 -> '2'. The exact inverse of `parseQtyToMilli`. */
export function formatMilli(qty: number): string {
  const sign = qty < 0 ? '-' : '';
  const abs = Math.abs(qty);
  const whole = Math.trunc(abs / 1000);
  const fraction = String(abs % 1000).padStart(3, '0').replace(/0+$/, '');
  return fraction === '' ? `${sign}${whole}` : `${sign}${whole}.${fraction}`;
}

/** VND is exponent-0: the price input IS the minor amount. Integer only. */
function parseVnd(input: string): number | null {
  const cleaned = input.trim().replace(/[.\s,]/g, '');
  if (!/^\d+$/.test(cleaned)) return null;
  const value = Number.parseInt(cleaned, 10);
  return Number.isSafeInteger(value) ? value : null;
}

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------

export function WarehouseShell({ onActivateDevice }: { readonly onActivateDevice: () => void }): React.JSX.Element {
  const api = useApi();
  const { me } = useAuth();
  const branchId = me?.session.activeBranchId ?? null;
  const [tab, setTab] = useState<Tab>('RECEIVE');
  const [warehouseId, setWarehouseId] = useState('');

  const warehouses = useAsync(
    (signal) => api.inventory.warehouses({ branchId: branchId ?? '' }, { signal }),
    [branchId],
    { enabled: branchId !== null },
  );

  if (branchId === null) {
    return (
      <Chrome title="Kho" onActivateDevice={onActivateDevice}>
        <ErrorState title="Chưa chọn chi nhánh" message="Chọn chi nhánh trong menu tài khoản." />
      </Chrome>
    );
  }

  const warehouseOptions = (warehouses.data?.warehouses ?? [])
    .filter((w) => w.isActive)
    .map((w) => ({ value: w.id, label: `${w.name} (${w.warehouseType})` }));

  const needsWarehouse = tab !== 'AVAILABILITY';

  return (
    <Chrome
      title="Kho"
      onActivateDevice={onActivateDevice}
      actions={
        <Tabs
          variant="pill"
          ariaLabel="Nghiệp vụ kho"
          value={tab}
          onChange={(id) => setTab(id as Tab)}
          items={[
            { id: 'RECEIVE', label: 'Nhận hàng' },
            { id: 'COUNT', label: 'Kiểm kê' },
            { id: 'ISSUE', label: 'Xuất NL' },
            { id: 'WASTAGE', label: 'Hao hụt' },
            { id: 'AVAILABILITY', label: 'Báo hết món' },
          ]}
        />
      }
    >
      <div className="h-full overflow-y-auto p-4">
        {needsWarehouse ? (
          <div className="mb-4 max-w-md">
            <Select
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              placeholder="— Chọn kho làm việc —"
              options={warehouseOptions}
              aria-label="Kho làm việc"
            />
            {warehouses.error !== null ? (
              <p className="text-flow mt-1 text-xs text-danger-fg">
                Không tải được danh sách kho (cần quyền inventory.view): {warehouses.error.message}
              </p>
            ) : null}
          </div>
        ) : null}

        {needsWarehouse && warehouseId === '' ? (
          <EmptyState
            title="Chọn kho trước"
            description="Mọi phiếu nhận hàng, kiểm kê, xuất và hao hụt đều ghi vào một kho cụ thể."
          />
        ) : null}

        {tab === 'RECEIVE' && warehouseId !== '' ? (
          <ReceivePanel branchId={branchId} warehouseId={warehouseId} />
        ) : null}
        {tab === 'COUNT' && warehouseId !== '' ? <CountPanel branchId={branchId} warehouseId={warehouseId} /> : null}
        {tab === 'ISSUE' && warehouseId !== '' ? <IssuePanel branchId={branchId} warehouseId={warehouseId} /> : null}
        {tab === 'WASTAGE' && warehouseId !== '' ? (
          <WastagePanel branchId={branchId} warehouseId={warehouseId} />
        ) : null}
        {tab === 'AVAILABILITY' ? <AvailabilityPanel branchId={branchId} /> : null}
      </div>
    </Chrome>
  );
}

// ---------------------------------------------------------------------------
// Shared: ingredient picker (search → tap to add)
// ---------------------------------------------------------------------------

function IngredientSearch({ onPick }: { readonly onPick: (ingredient: InvIngredient) => void }): React.JSX.Element {
  const api = useApi();
  const [query, setQuery] = useState('');
  const results = useAsync(
    (signal) => api.inventory.ingredients({ q: query, activeOnly: true, limit: 20 }, { signal }),
    [query],
    { enabled: query.length >= 1 },
  );

  return (
    <div className="space-y-2">
      <SearchField initialValue={query} onSearch={setQuery} placeholder="Tìm nguyên liệu (tên, mã, barcode)…" />
      {results.loading ? <LoadingState compact /> : null}
      <ul className="flex flex-wrap gap-2">
        {(results.data?.ingredients ?? []).map((ingredient) => (
          <li key={ingredient.id}>
            <Button size="sm" variant="secondary" onClick={() => onPick(ingredient)}>
              + {ingredient.nameVi} ({ingredient.stockUnitCode})
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Nhận hàng
// ---------------------------------------------------------------------------

interface ReceiveLine {
  readonly key: string;
  readonly ingredientId: string;
  readonly name: string;
  readonly unitCode: InvIngredient['purchaseUnitCode'];
  readonly poItemId: string | null;
  qty: string;
  price: string;
}

function ReceivePanel({
  branchId,
  warehouseId,
}: {
  readonly branchId: string;
  readonly warehouseId: string;
}): React.JSX.Element {
  const api = useApi();
  const toast = useToast();

  const suppliers = useAsync((signal) => api.inventory.suppliers({ activeOnly: true }, { signal }), []);
  const pos = useAsync(
    (signal) => api.inventory.purchaseOrders({ warehouseId, branchId }, { signal }),
    [warehouseId, branchId],
  );

  const [supplierId, setSupplierId] = useState('');
  const [poId, setPoId] = useState('');
  const [deliveryNoteNo, setDeliveryNoteNo] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<readonly ReceiveLine[]>([]);
  const [busy, setBusy] = useState(false);

  // Only POs a receipt can legally land on. DRAFT/CANCELLED/CLOSED are out.
  const receivablePos = useMemo<InvPurchaseOrder[]>(
    () =>
      (pos.data?.purchaseOrders ?? []).filter((po) =>
        ['APPROVED', 'SENT', 'PARTIALLY_RECEIVED'].includes(po.status),
      ),
    [pos.data],
  );

  const loadPo = async (id: string): Promise<void> => {
    setPoId(id);
    if (id === '') return;
    try {
      const detail = await api.inventory.purchaseOrder(id);
      setSupplierId(detail.purchaseOrder.supplierId);
      // Names come from the ingredient list; the PO item carries only the id.
      const named = await api.inventory.ingredients({ limit: 200 });
      const nameOf = new Map(named.ingredients.map((i) => [i.id, i.nameVi] as const));
      setLines(
        detail.items
          .filter((item: InvPoItem) => item.remainingQtyMilli > 0)
          .map((item: InvPoItem) => ({
            key: item.id,
            ingredientId: item.ingredientId,
            name: nameOf.get(item.ingredientId) ?? `NL …${item.ingredientId.slice(-6)}`,
            unitCode: item.unitCode,
            poItemId: item.id,
            qty: formatMilli(item.remainingQtyMilli),
            price: String(item.unitPriceMinor),
          })),
      );
    } catch (cause: unknown) {
      toast.error('Không tải được đơn mua hàng', errorMessage(cause));
    }
  };

  const addIngredient = (ingredient: InvIngredient): void => {
    setLines((previous) => [
      ...previous,
      {
        key: `${ingredient.id}-${Date.now().toString(36)}`,
        ingredientId: ingredient.id,
        name: ingredient.nameVi,
        unitCode: ingredient.purchaseUnitCode,
        poItemId: null,
        qty: '',
        price: String(ingredient.defaultCostMinor),
      },
    ]);
  };

  const patchLine = (key: string, patch: Partial<Pick<ReceiveLine, 'qty' | 'price'>>): void => {
    setLines((previous) => previous.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  const submit = async (): Promise<void> => {
    if (supplierId === '') {
      toast.error('Thiếu nhà cung cấp', 'Chọn nhà cung cấp (hoặc chọn đơn mua hàng để tự điền).');
      return;
    }
    const items: ReceiptItemInput[] = [];
    for (const line of lines) {
      const qtyMilli = parseQtyToMilli(line.qty);
      const priceMinor = parseVnd(line.price);
      if (qtyMilli === null || qtyMilli <= 0) {
        toast.error('Số lượng không hợp lệ', `Dòng "${line.name}": nhập số dương, tối đa 3 chữ số thập phân.`);
        return;
      }
      if (priceMinor === null) {
        toast.error('Đơn giá không hợp lệ', `Dòng "${line.name}": nhập số tiền VND nguyên.`);
        return;
      }
      items.push({
        ingredientId: line.ingredientId,
        purchaseOrderItemId: line.poItemId,
        receivedQtyMilli: qtyMilli,
        unitCode: line.unitCode,
        unitPriceMinor: priceMinor,
      });
    }
    if (items.length === 0) {
      toast.error('Phiếu trống', 'Thêm ít nhất một dòng hàng.');
      return;
    }
    setBusy(true);
    try {
      const result = await api.inventory.createReceipt({
        branchId,
        warehouseId,
        supplierId,
        purchaseOrderId: poId === '' ? null : poId,
        deliveryNoteNo: deliveryNoteNo.trim() === '' ? null : deliveryNoteNo.trim(),
        note: note.trim() === '' ? null : note.trim(),
        items,
      });
      toast.success(`Đã nhập kho — phiếu ${result.receipt.receiptNo}`, `Tổng ${vnd(result.receipt.totalMinor)}`);
      setLines([]);
      setPoId('');
      setDeliveryNoteNo('');
      setNote('');
      pos.reload();
    } catch (cause: unknown) {
      toast.error('Không nhập kho được', errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <Card title="Nhận hàng từ nhà cung cấp" description="Chọn đơn mua hàng để điền sẵn, hoặc nhập tự do.">
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            value={poId}
            onChange={(e) => void loadPo(e.target.value)}
            placeholder="— Nhận tự do (không theo PO) —"
            options={receivablePos.map((po) => ({
              value: po.id,
              label: `${po.poNo} · ${po.status} · ${vnd(po.totalMinor)}`,
            }))}
            aria-label="Đơn mua hàng"
          />
          <Select
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            placeholder="— Chọn nhà cung cấp —"
            options={(suppliers.data?.suppliers ?? []).map((s) => ({ value: s.id, label: s.name }))}
            aria-label="Nhà cung cấp"
          />
          <Input
            value={deliveryNoteNo}
            onChange={(e) => setDeliveryNoteNo(e.target.value)}
            placeholder="Số phiếu giao hàng (tuỳ chọn)"
          />
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ghi chú (tuỳ chọn)" />
        </div>
      </Card>

      <Card title="Thêm dòng hàng">
        <IngredientSearch onPick={addIngredient} />
      </Card>

      {lines.length > 0 ? (
        <Card title={`Hàng nhận (${lines.length} dòng)`}>
          <ul className="divide-y divide-line">
            {lines.map((line) => (
              <li key={line.key} className="flex flex-wrap items-center gap-2 py-2">
                <span className="text-flow min-w-0 flex-1 text-sm font-medium text-fg">
                  {line.name}
                  {line.poItemId !== null ? (
                    <Badge tone="info" size="sm" className="ml-2">
                      PO
                    </Badge>
                  ) : null}
                </span>
                <Input
                  className="w-28 text-right"
                  inputMode="decimal"
                  value={line.qty}
                  onChange={(e) => patchLine(line.key, { qty: e.target.value })}
                  placeholder="SL"
                  aria-label={`Số lượng ${line.name}`}
                />
                <span className="w-14 text-xs text-fg-subtle">{line.unitCode}</span>
                <Input
                  className="w-32 text-right"
                  inputMode="numeric"
                  value={line.price}
                  onChange={(e) => patchLine(line.key, { price: e.target.value })}
                  placeholder="Đơn giá"
                  aria-label={`Đơn giá ${line.name}`}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setLines((previous) => previous.filter((l) => l.key !== line.key))}
                >
                  Xoá
                </Button>
              </li>
            ))}
          </ul>
          <Button className="mt-3" size="touch" fullWidth loading={busy} onClick={() => void submit()}>
            Nhập kho
          </Button>
        </Card>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. Kiểm kê
// ---------------------------------------------------------------------------

function CountPanel({
  branchId,
  warehouseId,
}: {
  readonly branchId: string;
  readonly warehouseId: string;
}): React.JSX.Element {
  const api = useApi();
  const toast = useToast();

  const counts = useAsync((signal) => api.inventory.counts({ warehouseId }, { signal }), [warehouseId]);
  const open = (counts.data?.counts ?? []).find((c) => c.status === 'IN_PROGRESS' || c.status === 'DRAFT') ?? null;
  const pendingReview = (counts.data?.counts ?? []).find((c) => c.status === 'PENDING_REVIEW') ?? null;

  const detail = useAsync(
    (signal) => api.inventory.count(open?.id ?? '', { signal }),
    [open?.id],
    { enabled: open !== null },
  );

  // Names for the sheet — count items carry only ingredient ids.
  const ingredients = useAsync((signal) => api.inventory.ingredients({ limit: 200 }, { signal }), []);
  const nameOf = useMemo(
    () => new Map((ingredients.data?.ingredients ?? []).map((i) => [i.id, i.nameVi] as const)),
    [ingredients.data],
  );

  const [counted, setCounted] = useState<Readonly<Record<string, string>>>({});
  const [busy, setBusy] = useState(false);

  const start = async (): Promise<void> => {
    setBusy(true);
    try {
      const result = await api.inventory.startCount({ branchId, warehouseId });
      toast.success(`Đã mở phiếu kiểm kê ${result.count.countNo}`, `${result.itemCount} mặt hàng, tồn hệ thống đã chốt.`);
      setCounted({});
      counts.reload();
    } catch (cause: unknown) {
      toast.error('Không mở được phiếu kiểm kê', errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const save = async (thenSubmit: boolean): Promise<void> => {
    if (open === null) return;
    const items: { ingredientId: string; countedQtyMilli: number }[] = [];
    for (const [ingredientId, raw] of Object.entries(counted)) {
      if (raw.trim() === '') continue;
      const qtyMilli = parseQtyToMilli(raw);
      if (qtyMilli === null) {
        toast.error('Số đếm không hợp lệ', `"${nameOf.get(ingredientId) ?? ingredientId}": nhập số không âm, tối đa 3 chữ số thập phân.`);
        return;
      }
      items.push({ ingredientId, countedQtyMilli: qtyMilli });
    }
    if (items.length === 0) {
      toast.error('Chưa có dòng nào được đếm', 'Nhập số lượng đếm thực tế trước.');
      return;
    }
    setBusy(true);
    try {
      await api.inventory.saveCountItems(open.id, { items });
      if (thenSubmit) {
        const submitted = await api.inventory.submitCount(open.id);
        toast.success(
          `Đã gửi duyệt phiếu ${submitted.count.countNo}`,
          `${submitted.count.varianceItemCount} mục lệch, giá trị lệch ${vnd(submitted.count.totalVarianceValueMinor)}.`,
        );
        setCounted({});
      } else {
        toast.success('Đã lưu số đếm');
      }
      counts.reload();
      detail.reload();
    } catch (cause: unknown) {
      toast.error(thenSubmit ? 'Không gửi duyệt được' : 'Không lưu được số đếm', errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  if (counts.initialLoading) return <LoadingState label="Đang tải phiếu kiểm kê…" />;

  return (
    <div className="space-y-3">
      {pendingReview !== null ? (
        <Card title={`Phiếu ${pendingReview.countNo} đang chờ duyệt`}>
          <p className="text-flow text-sm text-fg-muted">
            {pendingReview.varianceItemCount} mục lệch · giá trị lệch {vnd(pendingReview.totalVarianceValueMinor)}.
            Quản lý duyệt trong Hộp thư duyệt (máy Quản lý ca) hoặc ứng dụng Quản trị.
          </p>
        </Card>
      ) : null}

      {open === null ? (
        <Card
          title="Bắt đầu kiểm kê"
          description="Tồn hệ thống được chốt tại thời điểm mở phiếu — bán hàng trong lúc đếm không làm xê dịch mục tiêu."
        >
          <Button size="touch" loading={busy} onClick={() => void start()} disabled={pendingReview !== null}>
            Mở phiếu kiểm kê kho này
          </Button>
          {pendingReview !== null ? (
            <p className="text-flow mt-2 text-xs text-fg-subtle">
              Phải chờ phiếu đang chờ duyệt được xử lý xong mới mở phiếu mới.
            </p>
          ) : null}
        </Card>
      ) : (
        <Card
          title={`Phiếu ${open.countNo}`}
          description="Nhập số đếm thực tế theo đơn vị kho (cho phép 3 chữ số thập phân). Dòng bỏ trống sẽ không được ghi."
          actions={<Badge tone="warning">{open.status}</Badge>}
        >
          {detail.initialLoading ? <LoadingState compact /> : null}
          {detail.error !== null ? <ErrorState message={detail.error.message} onRetry={detail.reload} /> : null}
          <ul className="divide-y divide-line">
            {(detail.data?.items ?? []).map((item: StockCountItem) => (
              <li key={item.id} className="flex flex-wrap items-center gap-2 py-2">
                <span className="text-flow min-w-0 flex-1 text-sm font-medium text-fg">
                  {nameOf.get(item.ingredientId) ?? `NL …${item.ingredientId.slice(-6)}`}
                </span>
                <span className="text-xs text-fg-subtle">
                  Hệ thống: {formatMilli(item.systemQtyMilli)} {item.unitCode}
                </span>
                <Input
                  className="w-28 text-right"
                  inputMode="decimal"
                  value={counted[item.ingredientId] ?? (item.countedQtyMilli === null ? '' : formatMilli(item.countedQtyMilli))}
                  onChange={(e) =>
                    setCounted((previous) => ({ ...previous, [item.ingredientId]: e.target.value }))
                  }
                  placeholder="Đếm được"
                  aria-label={`Số đếm ${nameOf.get(item.ingredientId) ?? item.ingredientId}`}
                />
              </li>
            ))}
          </ul>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Button size="touch" variant="secondary" loading={busy} onClick={() => void save(false)}>
              Lưu số đếm
            </Button>
            <Button size="touch" variant="success" loading={busy} onClick={() => void save(true)}>
              Lưu & gửi duyệt
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Xuất nguyên liệu
// ---------------------------------------------------------------------------

interface IssueLine {
  readonly key: string;
  readonly ingredientId: string;
  readonly name: string;
  readonly unitCode: string;
  qty: string;
}

function IssuePanel({
  branchId,
  warehouseId,
}: {
  readonly branchId: string;
  readonly warehouseId: string;
}): React.JSX.Element {
  const api = useApi();
  const toast = useToast();
  const [lines, setLines] = useState<readonly IssueLine[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    const items: { ingredientId: string; quantityMilli: number }[] = [];
    for (const line of lines) {
      const qtyMilli = parseQtyToMilli(line.qty);
      if (qtyMilli === null || qtyMilli <= 0) {
        toast.error('Số lượng không hợp lệ', `Dòng "${line.name}": nhập số dương.`);
        return;
      }
      items.push({ ingredientId: line.ingredientId, quantityMilli: qtyMilli });
    }
    if (items.length === 0) {
      toast.error('Phiếu trống', 'Thêm ít nhất một nguyên liệu.');
      return;
    }
    setBusy(true);
    try {
      const result = await api.inventory.createIssue({
        branchId,
        warehouseId,
        purpose: 'MANUAL',
        note: note.trim() === '' ? null : note.trim(),
        items,
      });
      toast.success('Đã xuất nguyên liệu', `Giá vốn xuất ${vnd(result.issue.totalCostMinor)} (FEFO).`);
      setLines([]);
      setNote('');
    } catch (cause: unknown) {
      toast.error('Không xuất được', errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <Card title="Xuất nguyên liệu cho bếp / bar" description="Xuất thủ công theo FEFO — lô cận hạn đi trước.">
        <IngredientSearch
          onPick={(ingredient) =>
            setLines((previous) => [
              ...previous,
              {
                key: `${ingredient.id}-${Date.now().toString(36)}`,
                ingredientId: ingredient.id,
                name: ingredient.nameVi,
                unitCode: ingredient.stockUnitCode,
                qty: '',
              },
            ])
          }
        />
      </Card>

      {lines.length > 0 ? (
        <Card title={`Nguyên liệu xuất (${lines.length})`}>
          <ul className="divide-y divide-line">
            {lines.map((line) => (
              <li key={line.key} className="flex items-center gap-2 py-2">
                <span className="text-flow min-w-0 flex-1 text-sm font-medium text-fg">{line.name}</span>
                <Input
                  className="w-28 text-right"
                  inputMode="decimal"
                  value={line.qty}
                  onChange={(e) =>
                    setLines((previous) =>
                      previous.map((l) => (l.key === line.key ? { ...l, qty: e.target.value } : l)),
                    )
                  }
                  placeholder="SL"
                  aria-label={`Số lượng ${line.name}`}
                />
                <span className="w-14 text-xs text-fg-subtle">{line.unitCode}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setLines((previous) => previous.filter((l) => l.key !== line.key))}
                >
                  Xoá
                </Button>
              </li>
            ))}
          </ul>
          <Input
            className="mt-3"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ghi chú (ví dụ: xuất cho ca tối)…"
          />
          <Button className="mt-3" size="touch" fullWidth loading={busy} onClick={() => void submit()}>
            Xuất kho
          </Button>
        </Card>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4. Báo hao hụt
// ---------------------------------------------------------------------------

const WASTAGE_LABEL: Readonly<Record<WastageType, string>> = {
  PREP_LOSS: 'Hao hụt sơ chế',
  COOKING_ERROR: 'Nấu hỏng',
  EXPIRED: 'Hết hạn',
  DAMAGED: 'Hư hỏng',
  SPILLED: 'Đổ vỡ',
  CUSTOMER_RETURN: 'Khách trả món',
  REMAKE: 'Làm lại món',
  QUALITY_CHECK: 'Kiểm tra chất lượng',
  OVER_PRODUCTION: 'Làm dư',
  OTHER: 'Khác',
};

function WastagePanel({
  branchId,
  warehouseId,
}: {
  readonly branchId: string;
  readonly warehouseId: string;
}): React.JSX.Element {
  const api = useApi();
  const toast = useToast();
  const [ingredient, setIngredient] = useState<InvIngredient | null>(null);
  const [qty, setQty] = useState('');
  const [wastageType, setWastageType] = useState<WastageType>('PREP_LOSS');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    if (ingredient === null) {
      toast.error('Chưa chọn nguyên liệu');
      return;
    }
    const qtyMilli = parseQtyToMilli(qty);
    if (qtyMilli === null || qtyMilli <= 0) {
      toast.error('Số lượng không hợp lệ', 'Nhập số dương, tối đa 3 chữ số thập phân.');
      return;
    }
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 3) {
      toast.error('Thiếu lý do', 'Báo hao hụt bắt buộc phải có lý do (ít nhất 3 ký tự) — được ghi nhật ký.');
      return;
    }
    setBusy(true);
    try {
      const result = await api.inventory.createWastage(
        {
          branchId,
          warehouseId,
          ingredientId: ingredient.id,
          quantityMilli: qtyMilli,
          wastageType,
          businessDate: todayIso(),
        },
        trimmedReason,
      );
      toast.success('Đã ghi nhận hao hụt', `Giá trị ${vnd(result.wastage.costMinor)} — chờ quản lý duyệt.`);
      setIngredient(null);
      setQty('');
      setReason('');
    } catch (cause: unknown) {
      toast.error('Không ghi nhận được', errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-3">
      <Card
        title="Báo hao hụt / hàng hỏng"
        description="Ghi nhận ngay khi phát hiện. Phiếu vào hàng chờ duyệt của quản lý; giá vốn tính theo FEFO."
      >
        {ingredient === null ? (
          <IngredientSearch onPick={setIngredient} />
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-fg">{ingredient.nameVi}</span>
            <Badge tone="neutral" size="sm">
              {ingredient.stockUnitCode}
            </Badge>
            <Button size="sm" variant="ghost" onClick={() => setIngredient(null)}>
              Chọn lại
            </Button>
          </div>
        )}

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Input
            inputMode="decimal"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder={`Số lượng${ingredient === null ? '' : ` (${ingredient.stockUnitCode})`}`}
            aria-label="Số lượng hao hụt"
          />
          <Select
            value={wastageType}
            onChange={(e) => setWastageType(e.target.value as WastageType)}
            options={WASTAGE_TYPES.map((t) => ({ value: t, label: WASTAGE_LABEL[t] }))}
            aria-label="Loại hao hụt"
          />
        </div>
        <Textarea
          className="mt-3"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="Lý do (bắt buộc, ghi nhật ký)…"
        />
        <Button className="mt-3" size="touch" fullWidth variant="danger" loading={busy} onClick={() => void submit()}>
          Ghi nhận hao hụt
        </Button>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 5. Báo hết món (unchanged — was already wired, offline-capable)
// ---------------------------------------------------------------------------

function AvailabilityPanel({ branchId }: { readonly branchId: string }): React.JSX.Element {
  const api = useApi();
  const toast = useToast();
  const outbox = useOutbox();
  const [search, setSearch] = useState('');
  const [reasonText, setReasonText] = useState('');

  const products = useAsync(
    (signal) =>
      api.menu.products({ pageSize: 60, status: 'ACTIVE', ...(search === '' ? {} : { search }) }, { signal }),
    [search],
  );

  const setAvailability = async (productId: string, name: string, isAvailable: boolean): Promise<void> => {
    const body = {
      branchId,
      isAvailable,
      businessDate: todayIso(),
      outOfStockReason: isAvailable ? null : 'NO_INGREDIENT',
      reasonText: reasonText === '' ? null : reasonText,
      clientTime: Date.now(),
    };
    try {
      await api.menu.setAvailability(productId, body);
      toast.success(isAvailable ? `Đã mở bán lại "${name}"` : `Đã báo hết "${name}"`);
      products.reload();
    } catch (cause: unknown) {
      if (!outbox.snapshot.online) {
        await outbox.queue({
          kind: 'PRODUCT_AVAILABILITY',
          label: `${isAvailable ? 'Mở bán' : 'Báo hết'} — ${name}`,
          branchId,
          request: { method: 'PATCH', path: `/v1/products/${productId}/availability`, body },
        });
        toast.warning('Đang ngoại tuyến', 'Thao tác đã lưu vào hàng đợi.');
        return;
      }
      toast.error('Không cập nhật được', errorMessage(cause));
    }
  };

  return (
    <div className="space-y-3">
      <Card title="Báo hết món / mở bán lại" description="Áp dụng cho ngày kinh doanh hôm nay tại chi nhánh này.">
        <div className="space-y-3">
          <SearchField initialValue={search} onSearch={setSearch} placeholder="Tìm món…" />
          <Input
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            placeholder="Lý do (ví dụ: hết thịt bò)…"
          />
        </div>
      </Card>

      <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {(products.data?.products ?? []).map((product) => (
          <li key={product.id} className="flex items-center gap-2 rounded-md bg-surface p-3 ring-1 ring-line">
            <span className="text-flow min-w-0 flex-1 text-sm font-medium text-fg">{product.nameVi}</span>
            <Button size="sm" variant="danger" onClick={() => void setAvailability(product.id, product.nameVi, false)}>
              Hết
            </Button>
            <Button size="sm" variant="success" onClick={() => void setAvailability(product.id, product.nameVi, true)}>
              Còn
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
